import React, { useCallback, useMemo } from 'react';
import {
  Button,
  backgroundColorNames,
  Badge,
  Card,
  CardFooter,
  CardHeader,
  CardSection,
  DateUtil,
  Flashy,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  ParagraphHeader,
  Radio,
  ReadonlyTextarea,
  SectionHeader,
  SmallHeader,
  Well,
} from '@axiom/ui';
import type { Opportunity, OpportunityOnboarding } from '@axiom/validation';
import type { UseOnboardingTextReturn, BadgeBackgroundType } from '@axiom/ui';
import { CountryCodesConst, CandidateOpportunitiesConst } from '@axiom/const';
import { CurrencyUtil } from '@axiom/utils';

import type { OnboardingCompBenefits as OnboardingCompBenefitsForm } from '../../schema/onboarding-comp-benefits-form-schema';
import { OnboardingCompBenefitsFormSchema } from '../../schema/onboarding-comp-benefits-form-schema';
import { OpportunityApi } from '../../api/opportunity-api';

const { CountryCodesAbbreviations: CountryCodeAbbr } = CountryCodesConst;

export type OnboardingCompBenefitsProps = {
  name: string;
  position: NonNullable<OpportunityOnboarding['positions']>[number];
  opportunityId: Opportunity['id'];
  benefitsText: Pick<
    NonNullable<ReturnType<UseOnboardingTextReturn['getOnboardingTexts']>>,
    'benefits' | 'holiday'
  >;
};

const TimeUnit = {
  HOURLY: 'Hourly',
  DAILY: 'Daily',
} as const;

export const OnboardingCompBenefits = ({
  name,
  position,
  opportunityId,
  benefitsText,
}: OnboardingCompBenefitsProps) => {
  const currencyType =
    position.candidateOpportunities?.[0]?.candidate.currencyType;

  const onboardingDetails =
    position.candidateOpportunities?.[0]?.onboardingDetails ?? null;

  const timeUnit = useMemo(() => {
    const countryCodes = [CountryCodeAbbr.US, CountryCodeAbbr.CA];
    const displayTimeUnit = onboardingDetails?.displayTimeUnit ?? null;

    if (displayTimeUnit) return displayTimeUnit;

    return countryCodes.includes(
      position.candidateOpportunities?.[0]?.candidate.addressCountryCode
    )
      ? TimeUnit.HOURLY
      : TimeUnit.DAILY;
  }, [onboardingDetails?.displayTimeUnit]);

  const rateDisplay = useCallback(
    (
      displayUnit: OnboardingCompBenefitsForm['displayTimeUnit'] | undefined
    ) => {
      const hourly =
        position.candidateOpportunities?.[0]?.proposedHourlyCompensation ?? 1;

      if (displayUnit === TimeUnit.DAILY) {
        const daily = hourly * (position.billingHoursPerDay ?? 0);
        return `${CurrencyUtil.getFormattedCurrency({ value: daily, currencyCode: currencyType }) ?? '$0'} per day`;
      }

      return `${CurrencyUtil.getFormattedCurrency({ value: hourly, currencyCode: currencyType }) ?? '$0'} per hour`;
    },
    [position, currencyType]
  );

  const baseAnnualSalary =
    position.candidateOpportunities?.[0]?.candidate.baseAnnualSalary;

  const displayTimeUnitUpdatedAt =
    onboardingDetails?.displayTimeUnitUpdatedAt ?? null;

  const getCompensationBadge = () => {
    const compensationType =
      position.candidateOpportunities?.[0]?.compensationType ?? null;
    const { EngagementSpecific, Premium } =
      CandidateOpportunitiesConst.CompensationTypes;
    let color: BadgeBackgroundType = backgroundColorNames.sky;
    let text: string;

    if (compensationType === EngagementSpecific) {
      text = EngagementSpecific;
    } else if (compensationType === Premium) {
      color = backgroundColorNames.green;
      text = Premium;
    } else {
      return null;
    }

    return (
      <Badge name="COMPENSATION_BADGE" background={color}>
        {text}
      </Badge>
    );
  };

  return (
    <Form
      name={name}
      schema={OnboardingCompBenefitsFormSchema}
      initialValues={{
        displayTimeUnit: timeUnit,
      }}
      onSubmit={async (_changedData, _actions, formData) => {
        if (position.candidateOpportunities?.[0]?.candidate.id) {
          await OpportunityApi.updateOpportunitiesOnboardingCandidate({
            opportunityId,
            candidateId: position.candidateOpportunities[0].candidate.id,
            body: formData,
          });

          await OpportunityApi.refreshOpportunitiesOnboarding({
            opportunityId,
          });
        }
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Card>
            <CardHeader>
              <SmallHeader>Compensation & Benefits</SmallHeader>
            </CardHeader>
            <CardSection>
              <Well>
                <Layout direction="horizontal">
                  <LayoutItem fluid>
                    <SectionHeader>Compensation</SectionHeader>
                  </LayoutItem>
                  <LayoutItem>{getCompensationBadge()}</LayoutItem>
                </Layout>
                <Gutter bottom="24px" />
                <Grid>
                  <GridRow columns="1" gutterBottom="16px">
                    <GridColumn>
                      <FormGroup>
                        <Radio
                          displayValue="Hourly Rate"
                          name="displayTimeUnit"
                          option={TimeUnit.HOURLY}
                        />
                        <Gutter bottom="8px" />
                        <Radio
                          displayValue="Daily Rate"
                          name="displayTimeUnit"
                          option={TimeUnit.DAILY}
                        />
                      </FormGroup>
                    </GridColumn>
                  </GridRow>
                  <GridRow columns="2" gutterBottom="16px">
                    <GridColumn>
                      <Layout>
                        <LayoutItem rightGutter="8px">
                          <ParagraphHeader>
                            Type of compensation:
                          </ParagraphHeader>
                        </LayoutItem>
                        <LayoutItem name="RESERVED_TYPE">
                          {position.reservedType || '--'}
                        </LayoutItem>
                      </Layout>
                    </GridColumn>
                    <GridColumn>
                      <Layout>
                        <LayoutItem rightGutter="8px">
                          <ParagraphHeader>
                            Annual Base Compensation:
                          </ParagraphHeader>
                        </LayoutItem>
                        <LayoutItem name="BASE_ANNUAL_SALARY">
                          {baseAnnualSalary
                            ? CurrencyUtil.getFormattedCurrency({
                                value: baseAnnualSalary,
                                currencyCode: currencyType,
                              })
                            : '--'}
                        </LayoutItem>
                      </Layout>
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn>
                      <Well name="RATE_DISPLAY" background="light">
                        The rate for this engagement on{' '}
                        {values.displayTimeUnit === TimeUnit.HOURLY
                          ? 'an '
                          : 'a '}
                        <Flashy bold>{values.displayTimeUnit}</Flashy> basis is{' '}
                        <Flashy bold>
                          {rateDisplay(values.displayTimeUnit)}
                        </Flashy>
                      </Well>
                    </GridColumn>
                  </GridRow>
                </Grid>
              </Well>
              {benefitsText.benefits ? (
                <>
                  <Gutter bottom="16px" />
                  <Well>
                    <SectionHeader>Benefits</SectionHeader>
                    <Gutter bottom="24px" />
                    <ReadonlyTextarea
                      name="BENEFITS"
                      value={benefitsText.benefits}
                      formatting="modern"
                    />
                  </Well>
                </>
              ) : null}
              {benefitsText.holiday ? (
                <>
                  <Gutter bottom="16px" />
                  <Well>
                    <SectionHeader>Holidays</SectionHeader>
                    <Gutter bottom="24px" />
                    <ReadonlyTextarea
                      name="HOLIDAYS"
                      value={benefitsText.holiday}
                      formatting="modern"
                    />
                  </Well>
                </>
              ) : null}
            </CardSection>
            <CardFooter>
              <Layout position="right middle">
                {displayTimeUnitUpdatedAt ? (
                  <>
                    <Flashy color="textSubtle" name="LAST_SAVED_TEXT">
                      Last saved on{' '}
                      {DateUtil.displayTimestamp(displayTimeUnitUpdatedAt)}
                    </Flashy>
                    <Gutter right="16px" />
                  </>
                ) : null}
                <Button
                  name="SAVE_BUTTON"
                  onClick={() => {
                    void fireSubmit();
                  }}
                >
                  Save
                </Button>
              </Layout>
            </CardFooter>
          </Card>
        );
      }}
    </Form>
  );
};
